import React from 'react';
import { MenuOutlined } from '@ant-design/icons';
import { DndContext } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import BaseTable from './BaseTable';

import './DragDropTableV2.scss';

const DraggableRow = ({ children, className, ...props }) => {
    const isDisabled = props['data-disabled'];
    const extraClassName = props['data-row-classname'] || '';
    const rowColor = props['data-row-color'];
    const hasValidColor = rowColor && rowColor !== '-1';
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: props['data-row-key'],
        disabled: isDisabled,
    });
    const style = {
        ...props.style,
        ...(hasValidColor
            ? { '--row-color': rowColor }
            : {}),
        transform: CSS.Transform.toString(
            transform && {
                ...transform,
                scaleY: 1,
            },
        ),
        transition,
        ...(isDragging
            ? {
                position: 'relative',
                zIndex: 9999,
            }
            : {}),
    };
    return (
        <tr {...props} ref={setNodeRef} style={style} className={[className, extraClassName].filter(Boolean).join(' ')} {...attributes}>
            {React.Children.map(children, (child) => {
                if (child.key === 'sort') {
                    return React.cloneElement(child, {
                        children: (
                            <MenuOutlined
                                ref={setActivatorNodeRef}
                                style={{
                                    touchAction: 'none',
                                    cursor: isDisabled ? 'default' : 'move',
                                }}
                                {...(!isDisabled ? listeners : {})}
                            />
                        ),
                    });
                }
                return child;
            })}
        </tr>
    );
};

const DragDropTableV2 = ({
    dataSource,
    onDragEnd,
    onChange,
    rowSelection,
    columns,
    loading,
    pagination,
    rowClassName,
    rowKey = (record) => record.id,
    onRow: onRowProp,
    borderColor = false,
    getRowColor,
    rowGap = 6,  
    ...props
}) => {
    const isDragDisabled = dataSource?.length <= 1;

    return (
        <div className={borderColor ? 'drag-drop-table' : undefined}  style={{ '--row-gap': `${rowGap}px` }} >
            <DndContext onDragEnd={({ active, over }) => onDragEnd({ active, over })}>
                <SortableContext
                    items={dataSource.map((i) => i.id)}
                    strategy={verticalListSortingStrategy}
                >
                    <BaseTable
                        components={{
                            body: {
                                row: DraggableRow,
                            },
                        }}
                        onRow={(record) => {
                            const color = getRowColor?.(record);

                            const baseProps = {
                                'data-disabled': isDragDisabled,
                                'data-row-classname': rowClassName ? rowClassName(record) : '',
                                'data-row-color': color ? color : '',
                            };
                            const parentProps = onRowProp ? onRowProp(record) : {};
                            return {
                                ...baseProps,
                                ...parentProps,
                                style: {
                                    ...parentProps.style,
                                },
                                className: [baseProps['data-row-classname'], parentProps.className].filter(Boolean).join(' '),
                            };
                        }}
                        rowSelection={rowSelection}
                        loading={loading}
                        pagination={pagination}
                        onChange={onChange}
                        rowKey={rowKey}
                        columns={columns}
                        dataSource={dataSource}
                        scroll={null}
                        {...props}
                    />
                </SortableContext>
            </DndContext>
        </div>
    );
};
export default DragDropTableV2;
